import { useEffect, useState } from 'react';
import { reverseGeocodeCached } from '../utils/geocode';

/**
 * Given a { lat, lng } object, returns a human-readable label.
 * Prefers coordinates.label if present (already stored).
 * Otherwise reverse-geocodes on the fly (and caches).
 *
 * Usage:
 *   const label = useLocationLabel(coords);
 */
export const useLocationLabel = (coordinates) => {
  const [label, setLabel] = useState(coordinates?.label || null);

  useEffect(() => {
    let cancelled = false;

    if (!coordinates) {
      setLabel(null);
      return;
    }

    // Already has a stored label — use it
    if (coordinates.label) {
      setLabel(coordinates.label);
      return;
    }

    // No coords — nothing to do
    if (coordinates.lat == null || coordinates.lng == null) {
      setLabel(null);
      return;
    }

    // Reverse-geocode in the background
    (async () => {
      const resolved = await reverseGeocodeCached(coordinates.lat, coordinates.lng);
      if (!cancelled) setLabel(resolved);
    })();

    return () => { cancelled = true; };
  }, [coordinates?.lat, coordinates?.lng, coordinates?.label]);

  return label;
};

/**
 * Same idea but accepts raw lat/lng numbers.
 */
export const useCoordsLabel = (lat, lng, existingLabel) => {
  return useLocationLabel(
    lat != null && lng != null ? { lat, lng, label: existingLabel } : null
  );
};